import { useMemo, useState } from 'react';
import { Save, Trash2 } from 'lucide-react';
import type { SupportedSymbol } from '@/lib/symbols';
import type { UserWatchlist } from '@/lib/watchlists';
import { moveItem, watchlistButtonClass, watchlistInputClass } from './watchlistEditor.utils';
import { WatchlistSymbolRow } from './WatchlistSymbolRow';

interface WatchlistSymbolEditorProps {
  watchlist: UserWatchlist;
  catalog: readonly SupportedSymbol[];
  maxNameLength: number;
  isMutating: boolean;
  onRename: (name: string) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
  onReplaceSymbols: (tickers: readonly string[]) => Promise<unknown>;
}

export function WatchlistSymbolEditor({
  watchlist,
  catalog,
  maxNameLength,
  isMutating,
  onRename,
  onDelete,
  onReplaceSymbols,
}: WatchlistSymbolEditorProps) {
  const [name, setName] = useState(watchlist.name);
  const [symbolQuery, setSymbolQuery] = useState('');
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const tickers = watchlist.symbols.map((symbol) => symbol.ticker);
  const catalogByTicker = useMemo(
    () => new Map(catalog.map((symbol) => [symbol.ticker, symbol])),
    [catalog],
  );
  const run = (operation: Promise<unknown>) => void operation.catch(() => {});

  const addableSymbols = useMemo(() => {
    const current = new Set(tickers);
    const query = symbolQuery.trim().toUpperCase();
    return catalog
      .filter(
        (symbol) =>
          !current.has(symbol.ticker) &&
          (query === '' ||
            symbol.ticker.includes(query) ||
            symbol.label.toUpperCase().includes(query)),
      )
      .slice(0, 8);
  }, [catalog, symbolQuery, tickers]);

  return (
    <section className="p-4">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          aria-label="Watchlist name"
          value={name}
          maxLength={maxNameLength}
          onChange={(event) => setName(event.target.value)}
          className={`${watchlistInputClass} max-w-xs`}
        />
        <button
          type="button"
          disabled={isMutating || name.trim() === '' || name === watchlist.name}
          onClick={() => run(onRename(name))}
          className={watchlistButtonClass}
        >
          <Save className="h-3.5 w-3.5" /> Rename
        </button>
        <button
          type="button"
          disabled={isMutating}
          onClick={() => {
            if (deleteConfirmed) {
              run(onDelete());
            } else {
              setDeleteConfirmed(true);
            }
          }}
          className={`${watchlistButtonClass} text-red-300`}
        >
          <Trash2 className="h-3.5 w-3.5" />
          {deleteConfirmed ? 'Confirm delete' : 'Delete'}
        </button>
      </div>

      <div className="mb-4 rounded border border-zinc-800">
        {watchlist.symbols.map((saved, index) => {
          const symbol = catalogByTicker.get(saved.ticker);
          return (
            <WatchlistSymbolRow
              key={saved.ticker}
              ticker={saved.ticker}
              label={symbol?.label ?? null}
              index={index}
              symbolCount={tickers.length}
              isMutating={isMutating}
              onMove={(from, to) => run(onReplaceSymbols(moveItem(tickers, from, to)))}
              onRemove={() => {
                if (tickers.length === 1) {
                  const shouldDelete = window.confirm(
                    `Removing ${saved.ticker} will also delete the watchlist "${watchlist.name}". Continue?`,
                  );
                  if (shouldDelete) {
                    run(onDelete());
                  }
                  return;
                }
                run(onReplaceSymbols(tickers.filter((ticker) => ticker !== saved.ticker)));
              }}
            />
          );
        })}
        {watchlist.symbols.length === 0 && (
          <div className="p-4 font-mono text-[10px] text-zinc-500">No saved symbols</div>
        )}
      </div>

      <label className="block font-mono text-[10px] text-zinc-500">
        Add from enabled catalog
        <input
          value={symbolQuery}
          onChange={(event) => setSymbolQuery(event.target.value)}
          placeholder="Search ticker or company"
          className={`${watchlistInputClass} mt-1`}
        />
      </label>
      {symbolQuery.trim() !== '' && (
        <div className="mt-1 max-h-44 overflow-y-auto rounded border border-zinc-800 bg-zinc-950 py-1">
          {addableSymbols.map((symbol) => (
            <button
              key={symbol.ticker}
              type="button"
              aria-label={`Add ${symbol.ticker}`}
              disabled={isMutating}
              onClick={() => {
                run(onReplaceSymbols([...tickers, symbol.ticker]));
                setSymbolQuery('');
              }}
              className="flex h-9 w-full items-center gap-3 px-3 text-left font-mono text-[10px] text-zinc-400 hover:bg-white/[.05] hover:text-zinc-100"
            >
              <span className="w-14 font-semibold">{symbol.ticker}</span>
              <span className="truncate">{symbol.label}</span>
            </button>
          ))}
          {addableSymbols.length === 0 && (
            <div className="px-3 py-2 font-mono text-[10px] text-zinc-600">
              No enabled symbols found
            </div>
          )}
        </div>
      )}
    </section>
  );
}
